import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { AddressService, type Address } from '../../services/address.service';
import { AuthService } from '../../services/auth.service';
import { CartService } from '../../services/cart.service';
import { PaymentsService } from '../../services/payments.service';

type Step = 1 | 2 | 3;

interface ShippingOption {
  id: string;
  label: string;
  description: string;
  price: number;
}

@Component({
  selector: 'app-checkout',
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.scss'
})
export class CheckoutComponent {
  private readonly fb = inject(FormBuilder);
  private readonly cart = inject(CartService);
  private readonly auth = inject(AuthService);
  private readonly payments = inject(PaymentsService);
  private readonly addressService = inject(AddressService);

  readonly step = signal<Step>(1);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  readonly isLoggedIn = this.auth.isLoggedIn;
  readonly addresses = this.addressService.addresses;
  readonly selectedAddressId = signal<number | null>(null);
  readonly showNewAddressForm = signal(false);
  readonly savingAddress = signal(false);

  readonly shippingOptions: ShippingOption[] = [
    { id: 'standard', label: 'Envío Estándar', description: '5-7 días hábiles', price: 0 }
  ];

  readonly selectedShipping = signal<ShippingOption>(this.shippingOptions[0]);
  readonly paymentMethod = signal<'stripe' | 'mercadopago'>('stripe');

  readonly shippingForm = this.fb.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phone: ['', [Validators.required, Validators.pattern(/^\d{10}$/)]],
    address: ['', Validators.required],
    city: ['', Validators.required],
    state: ['', Validators.required],
    zip: ['', [Validators.required, Validators.pattern(/^\d{5}$/)]]
  });

  readonly items = this.cart.items;
  readonly subtotal = this.cart.subtotal;
  readonly shippingCost = computed(() => this.selectedShipping().price);
  readonly total = computed(() => this.subtotal() + this.shippingCost());

  constructor() {
    if (this.auth.isLoggedIn()) {
      this.addressService.load();
    }

    const user = this.auth.currentUser();
    if (user) {
      const parts = user.name.trim().split(' ');
      this.shippingForm.patchValue({
        firstName: parts[0] ?? '',
        lastName: parts.slice(1).join(' '),
        email: user.email
      });
    }
  }

  selectAddress(addr: Address): void {
    this.selectedAddressId.set(addr.id);
    this.showNewAddressForm.set(false);
    const parts = addr.full_name.trim().split(' ');
    this.shippingForm.patchValue({
      firstName: parts[0] ?? '',
      lastName: parts.slice(1).join(' '),
      phone: addr.phone,
      address: addr.street,
      city: addr.city,
      state: addr.state,
      zip: addr.zip_code
    });
  }

  toggleNewAddressForm(): void {
    this.showNewAddressForm.set(true);
    this.selectedAddressId.set(null);
    this.shippingForm.patchValue({ address: '', city: '', state: '', zip: '', phone: '' });
  }

  saveAndSelectAddress(): void {
    this.shippingForm.markAllAsTouched();
    if (this.shippingForm.invalid) return;
    const v = this.shippingForm.getRawValue();
    this.savingAddress.set(true);
    this.addressService.create({
      full_name: `${v.firstName} ${v.lastName}`.trim(),
      phone: v.phone ?? '',
      street: v.address ?? '',
      city: v.city ?? '',
      state: v.state ?? '',
      zip_code: v.zip ?? '',
      is_default: this.addresses().length === 0
    }).subscribe({
      next: (addr) => {
        this.addressService.load();
        this.selectedAddressId.set(addr.id);
        this.showNewAddressForm.set(false);
        this.savingAddress.set(false);
        this.step.set(2);
      },
      error: () => this.savingAddress.set(false)
    });
  }

  nextStep(): void {
    if (this.step() === 1) {
      const pickSavedAddress =
        this.isLoggedIn() && this.addresses().length > 0 && !this.showNewAddressForm();

      if (pickSavedAddress) {
        if (this.selectedAddressId() === null) return;
      } else {
        this.shippingForm.markAllAsTouched();
        if (this.shippingForm.invalid) return;

        if (this.isLoggedIn()) {
          this.saveAndSelectAddress();
          return;
        }
      }
    }
    this.step.update((s) => (Math.min(s + 1, 3) as Step));
  }

  prevStep(): void {
    this.step.update((s) => (Math.max(s - 1, 1) as Step));
  }

  goToStep(target: Step): void {
    if (target < this.step()) this.step.set(target);
  }

  confirm(): void {
    if (this.loading()) return;

    const cartItems = this.items();
    if (!cartItems.length) return;

    const email = this.auth.currentUser()?.email ?? this.shippingForm.get('email')?.value ?? '';
    if (!email) return;

    this.loading.set(true);
    this.error.set(null);

    const payload = {
      items: cartItems.map((item) => ({
        product_id: Number(item.productId),
        name: item.name,
        unit_price: item.unitPrice,
        quantity: item.qty,
      })),
      shipping_cost: this.shippingCost(),
      customer_email: email,
      address_id: this.selectedAddressId(),
    };

    this.payments.createCheckoutSession(payload).subscribe({
      next: ({ url, session_id }) => {
        localStorage.setItem('kota_pending_session', session_id);
        window.location.href = url;
      },
      error: (err) => {
        this.loading.set(false);
        this.error.set(err?.error?.message ?? 'No se pudo iniciar el pago. Intenta de nuevo.');
      },
    });
  }

  isInvalid(field: string): boolean {
    const ctrl = this.shippingForm.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }

  formatMoney(n: number): string {
    return '$' + n.toLocaleString('es-MX');
  }
}
